import { isOrchestrationStructure } from './is-orchestration-structure';
import { parsePluginOutput } from './parse-plugin-output'; // eslint-disable-next-line no-barrel-files/no-barrel-files
import type {
	FunctionResultType,
	MinionOutput,
	MinionPluginInvocationInfo,
	OrchestrationRound,
	ParsedMinionData,
	ParsedPluginOutput,
	PluginExtraInfo,
} from './types';

type ParsedMarkdownLink = {
	name: string;
	url: string;
	accountId: string | null;
};

/**
 * Parses a markdown-style link and extracts the display name and accountId.
 *
 * Handles links in format: [Name](/people/accountId)
 * Returns the name with accountId appended in parentheses if found.
 *
 * @param text - The text that may contain a markdown link
 * @returns Object with name, url, accountId, or null if not a valid link
 */
export const parseMarkdownLink = (text: string): ParsedMarkdownLink | null => {
	const match = text.match(/\[([^\]]+)\]\(([^)]+)\)/);
	if (!match) {
		return null;
	}

	const [, name, url] = match;
	const accountIdMatch = url.match(/\/people\/([^/]+)/);
	const accountId = accountIdMatch ? accountIdMatch[1] : null;

	return { name, url, accountId };
};

/**
 * Parses minion_extra_info to extract displayable data.
 *
 * The minion_extra_info can contain different structures depending on the minion type:
 *
 * 1. Search Minion with functionMessage:
 *    - Extracts pluginInvocationInfo.extraInfo containing searchResults/rankedDocuments
 *    - Stores in searchExtraInfo for specialized rendering
 *
 * 2. Non-Search Minion with functionMessage:
 *    - Extracts pluginInvocationInfo.output or functionMessage.output
 *    - Attempts to parse it using parsePluginOutput for structured display
 *
 * 3. Teamwork Agent with results array:
 *    - Extracts the results array containing function invocation results
 *    - Each result has pluginInvocationInfo with name, status, and output
 *
 * 4. Content fallback:
 *    - If none of the above, extracts the content field as plain text
 *
 * 5. Parse failure:
 *    - If JSON parsing fails, displays the raw minion_extra_info string
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const parseMinionExtraInfo = (
	minionExtraInfo: string | undefined,
	minionId: string | undefined,
	isSearchMinion: boolean,
): ParsedMinionData => {
	let extraInfo: MinionPluginInvocationInfo | string | null = null;
	let parsedOutput: ParsedPluginOutput | null = null;
	let functionResults: FunctionResultType[] | null = null;
	let searchExtraInfo: PluginExtraInfo | null = null;

	if (!minionExtraInfo) {
		return { extraInfo, parsedOutput, functionResults, searchExtraInfo };
	}

	try {
		const parsedExtraInfo = JSON.parse(minionExtraInfo);

		// Case 1 & 2: Has functionMessage (single function call result)
		if (parsedExtraInfo.functionMessage) {
			const { pluginInvocationInfo, output } = parsedExtraInfo.functionMessage;

			if (isSearchMinion && pluginInvocationInfo?.extraInfo) {
				// Search minion: extract nested search results from extraInfo
				try {
					searchExtraInfo = JSON.parse(pluginInvocationInfo.extraInfo);
				} catch {
					extraInfo = pluginInvocationInfo.extraInfo;
				}
			} else {
				// Non-search minion: parse the output for structured display
				const rawOutput = pluginInvocationInfo?.output || output;
				if (typeof rawOutput === 'string') {
					parsedOutput = parsePluginOutput(minionId, rawOutput);
					if (!parsedOutput) {
						extraInfo = rawOutput;
					}
				}
			}
		}
		// Case 3: Has results array (multiple function calls, e.g., Teamwork Agent)
		else if (Array.isArray(parsedExtraInfo.results) && parsedExtraInfo.results.length > 0) {
			functionResults = parsedExtraInfo.results as FunctionResultType[];
		}
		// Case 4: Has content field (plain text fallback)
		else if (parsedExtraInfo.content) {
			extraInfo = parsedExtraInfo.content;
		}
	} catch {
		// Case 5: JSON parsing failed, use raw string
		extraInfo = minionExtraInfo;
	}

	return { extraInfo, parsedOutput, functionResults, searchExtraInfo };
};

/**
 * Organizes minion outputs into orchestration rounds.
 * If the structure is already rounds (array of arrays), returns as-is.
 * If it's a flat array, wraps it in a single round.
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getOrchestrationRounds = (
	minionOutputs: MinionOutput[] | undefined,
): OrchestrationRound[] => {
	if (!Array.isArray(minionOutputs) || minionOutputs.length === 0) {
		return [];
	}

	// Check if it's already a rounds structure (array of arrays)
	if (isOrchestrationStructure(minionOutputs)) {
		return minionOutputs as OrchestrationRound[];
	}

	// Wrap flat array in a single round
	return [minionOutputs];
};
